// components/interview-page/InterviewGuardStatus.jsx
import { useState } from "react"
import icon from "../assets/interviewguard.png"

/**
 * Status values driven by InterviewGuardService callbacks:
 *   "idle"       — candidate hasn't verified yet (onVerified never fired)
 *   "active"     — verified, monitoring in progress (onVerified fired)
 *   "alert"      — suspicious activity detected (onAlert fired)
 *   "clear"      — alert dismissed (onClear fired, back to active)
 */
const InterviewGuardStatus = ({ status = "idle", alertMessage = "" }) => {
  const [hovered, setHovered] = useState(false)

  const config = {
    idle: {
      dot: "#9ca3af",
      label: "InterviewGuard",
      sub: "Monitoring inactive",
      bg: "rgba(0,0,0,0.45)",
      border: "rgba(255,255,255,0.08)",
      iconOpacity: 0.4,
    },
    active: {
      dot: "#4ade80",
      label: "InterviewGuard",
      sub: "Active · Session monitored",
      bg: "rgba(0,0,0,0.45)",
      border: "rgba(74,222,128,0.25)",
      iconOpacity: 1,
    },
    alert: {
      dot: "#fbbf24",
      label: "InterviewGuard",
      sub: alertMessage || "Suspicious activity detected",
      bg: "rgba(0,0,0,0.55)",
      border: "rgba(251,191,36,0.35)",
      iconOpacity: 1,
    },
    clear: {
      dot: "#4ade80",
      label: "InterviewGuard",
      sub: "Active · Session monitored",
      bg: "rgba(0,0,0,0.45)",
      border: "rgba(74,222,128,0.25)",
      iconOpacity: 1,
    },
  }

  const c = config[status] ?? config.idle

  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      {/* Main pill */}
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          padding: "6px 15px 6px 8px",
          borderRadius: 999,
          background: c.bg,
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          transition: "border-color 0.3s, background 0.3s",
          userSelect: "none",
          cursor: "default",
        }}
      >
        {/* Icon */}
        <img
          src={icon}
          alt="InterviewGuard"
          style={{
            width: 20,
            height: 20,
            objectFit: "contain",
            opacity: c.iconOpacity,
            borderRadius: 20,
            transition: "opacity 0.3s",
            flexShrink: 0,
          }}
        />

        {/* Text */}
        <span style={{ display: "flex", flexDirection: "column", gap: 0, lineHeight: 1 }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.45)",
              fontFamily: "'IBM Plex Mono', monospace",
            }}
          >
            {c.label}
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 500,
              color:
                status === "alert"
                  ? "#fbbf24"
                  : status === "idle"
                    ? "rgba(255,255,255,0.4)"
                    : "rgba(255,255,255,0.85)",
              fontFamily: "'DM Sans', sans-serif",
              marginTop: 2,
              maxWidth: 180,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {c.sub}
          </span>
        </span>
      </div>

      {/* Tooltip — idle only */}
      {status === "idle" && hovered && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            background: "rgba(15,15,15,0.92)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            border: "0.5px solid rgba(255,255,255,0.1)",
            borderRadius: 8,
            padding: "7px 11px",
            whiteSpace: "nowrap",
            pointerEvents: "none",
            zIndex: 100,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 12,
              fontWeight: 500,
              color: "rgba(255,255,255,0.75)",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Candidate hasn't installed the monitoring binary
          </p>
        </div>
      )}

      <style>{`
        @keyframes ig-pulse {
          0%, 100% { transform: scale(1); opacity: 0.35; }
          50% { transform: scale(2.2); opacity: 0; }
        }
      `}</style>
    </div>
  )
}

export default InterviewGuardStatus